// All MUI component overrides and legacy component settings.
// Receives `palette` from createPalette() so colors stay synchronized.

import type { Components, Palette } from '@mui/material/styles';

// ---- Component factory ----

export default function createComponents(palette: Palette): Components {
  return {
    // ---- MUI standard overrides ----

    MuiModal: {
      defaultProps: {
        // Allows typing into Intercom while a dialog is open.
        // Side effect: may reduce focus-trap accessibility enforcement.
        disableEnforceFocus: true,
      },
    },
    MuiInputBase: {
      defaultProps: {
        inputProps: {
          autoCorrect: 'off',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        legend: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${palette.border.subtle}`,
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          letterSpacing: '0.01071em',
        },
        underline: {
          '&:after': {
            borderBottomColor: palette.secondary.light,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        outlined: {
          '&$shrink': {
            backgroundColor: palette.background.paper,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: palette.surface.overlay,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: 24,
          borderBottom: `1px solid ${palette.border.subtle}`,
          backgroundColor: palette.surface.dialogTitle,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { 
          padding: 24, 
          borderBottom: `1px solid ${palette.border.subtle}`, 
          '.MuiDialogTitle-root + &': { 
            paddingTop: 24,
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        spacing: { padding: 24 },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'standard',
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'standard',
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: palette.surface.overlay,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: palette.state.hover.default,
          },
          '&.Mui-focusVisible': {
            backgroundColor: palette.state.hover.default,
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: palette.state.hover.default,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: palette.secondary.light,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  };
}
